
import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Send, CheckCircle, Clock, AlertCircle, User as UserIcon, Shield } from 'lucide-react';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { Ticket, TicketStatus, User, UserRole } from '@/shared/types';
import { isSectionAdmin } from '@/modules/identity/application/ability';
import { Button } from '../atoms/Button';

interface TicketDetailProps {
  ticket: Ticket;
  currentUser: User;
  onBack: () => void;
  onAddComment: (text: string) => void;
  onStatusChange: (status: TicketStatus) => void;
  canUpdate?: boolean;
}

export const TicketDetail: React.FC<TicketDetailProps> = ({ ticket, currentUser, onBack, onAddComment, onStatusChange, canUpdate = true }) => {
  const { t } = useI18n();
  const { formatDateTime, formatTime } = useDateFormatter();
  const [comment, setComment] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket.comments]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canUpdate) return;
    if (!comment.trim()) return;
    onAddComment(comment);
    setComment('');
  };

  const canChangeStatus = canUpdate && (currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.GUARD || isSectionAdmin(currentUser));

  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.NEW: return 'bg-blue-100 text-blue-800';
      case TicketStatus.IN_REVIEW: return 'bg-amber-100 text-amber-800';
      case TicketStatus.IN_PROCESS: return 'bg-purple-100 text-purple-800';
      case TicketStatus.RESOLVED: return 'bg-emerald-100 text-emerald-800';
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden animate-in slide-in-from-right-8 duration-300">
      
      {/* Header */}
      <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
            <ArrowLeft size={20} className="text-slate-600"/>
          </button>
          <div>
            <h3 className="font-bold text-slate-800">{ticket.title}</h3>
            <div className="flex items-center gap-2 text-xs">
              <span className={`px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${getStatusColor(ticket.status)}`}>
                 {t(`tickets.status.${ticket.status.toLowerCase()}`)}
              </span>
              <span className="text-slate-400">#{ticket.id}</span>
            </div>
            {ticket.userName && (
              <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                <UserIcon size={12} />
                <span>
                  {t('tickets.reportedBy')} <span className="font-semibold text-slate-700">{ticket.userName}</span>
                  {ticket.unitName && ` · ${ticket.unitName}`}
                </span>
              </div>
            )}
          </div>
        </div>

        {canChangeStatus && (
          <select 
             className="text-xs border-slate-300 rounded-lg p-2 bg-white shadow-sm"
             value={ticket.status}
             onChange={(e) => onStatusChange(e.target.value as TicketStatus)}
          >
             <option value={TicketStatus.NEW}>{t('tickets.status.new')}</option>
             <option value={TicketStatus.IN_REVIEW}>{t('tickets.status.in_review')}</option>
             <option value={TicketStatus.IN_PROCESS}>{t('tickets.status.in_process')}</option>
             <option value={TicketStatus.RESOLVED}>{t('tickets.status.resolved')}</option>
          </select>
        )}
      </div>

      {/* Content Scrollable Area */}
      <div className="flex-1 overflow-y-auto bg-slate-50/50 p-4 space-y-6">
        
        {/* Main Info Card */}
        <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
           <p className="text-slate-700 leading-relaxed mb-4">{ticket.description}</p>
           {ticket.attachmentUrl && (
             <div className="mt-4 rounded-lg overflow-hidden border border-slate-200 max-w-sm">
               <img src={ticket.attachmentUrl} alt="Evidence" className="w-full h-auto" />
             </div>
           )}
           <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-400 flex justify-between">
              <span>{formatDateTime(ticket.date)}</span>
              <span>Categoria: {t(`tickets.category.${ticket.category.toLowerCase()}`)}</span>
           </div>
        </div>

        {/* Timeline / Chat */}
        <div className="space-y-4 pb-4">
           {ticket.comments.map((msg) => {
             const isMe = msg.userId === currentUser.id;
             const isStaff = msg.isAdmin; 
             
             return (
               <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                 <div className={`max-w-[80%] rounded-2xl p-4 shadow-sm border ${
                    isMe 
                    ? 'bg-primary-600 text-white border-primary-600 rounded-br-none' 
                    : isStaff 
                    ? 'bg-white border-amber-200 rounded-bl-none'
                    : 'bg-white border-slate-100 rounded-bl-none'
                 }`}>
                   <div className="flex items-center gap-2 mb-1">
                      {isStaff && !isMe && <Shield size={12} className="text-amber-500"/>}
                      <span className={`text-xs font-bold ${isMe ? 'text-primary-100' : 'text-slate-500'}`}>
                        {msg.userName}
                      </span>
                      <span className={`text-[10px] ${isMe ? 'text-primary-200' : 'text-slate-400'}`}>
                        {formatTime(msg.date)}
                      </span>
                   </div>
                   <p className={`text-sm ${isMe ? 'text-white' : 'text-slate-700'}`}>{msg.text}</p>
                 </div>
               </div>
             )
           })}
           <div ref={chatEndRef} />
        </div>

      </div>

      {/* Input Area */}
      {canUpdate && (
      <div className="p-4 bg-white border-t border-slate-100">
         <form onSubmit={handleSubmit} className="flex gap-2">
            <input 
              className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all"
              placeholder={t('tickets.chat.placeholder')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <Button 
               type="submit" 
               className="rounded-full w-10 h-10 p-0 flex items-center justify-center" 
               disabled={!comment.trim()}
            >
              <Send size={18} />
            </Button>
         </form>
      </div>
      )}

    </div>
  );
};
